import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";

import { InlineNodeModel } from "./InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeId, NodeModel } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type {
  ComputedReferenceNodeProps,
  ComputedReferenceNodeConstructorProps,
} from "@corca/data";

export class ComputedReferenceNodeModel
  extends BaseNodeModel<ComputedReferenceNodeProps>
  implements VisitableNode
{
  static type = "computedReference" as const;

  type = ComputedReferenceNodeModel.type;

  readonly isAtom = true;

  protected fillRequiredProps(
    props: ComputedReferenceNodeConstructorProps,
  ): ComputedReferenceNodeProps {
    return { targetRefId: "", ...props };
  }

  static create(
    doc: DocumentModel,
    props: ComputedReferenceNodeProps,
    nodes: InlineNodeModel | NodeModel[] = [],
    id?: NodeId,
  ) {
    const inline = Array.isArray(nodes) ? doc.create(InlineNodeModel, nodes) : nodes;
    return new ComputedReferenceNodeModel(doc, id, { ...props, inlineId: inline.id }, [inline]);
  }

  get targetRefId(): string {
    return this.props.targetRefId;
  }

  /** Inline node holding the cached value, resolved via the `inlineId` LWW pointer. */
  get inlineNode(): InlineNodeModel | null {
    const { inlineId } = this.props;
    const pointed = inlineId ? this.children.find((n) => n.id === inlineId) : undefined;
    const node = pointed ?? this.children.find((n) => n.is(InlineNodeModel));
    return node?.is(InlineNodeModel) ? node : null;
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitComputedReferenceNode(this);
  }
}
